import { useSearchParams } from "react-router-dom";

export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  return {
    clearAllParams: () => setSearchParams(new URLSearchParams()),
    setSelectedBseTab: (tab: BseBuyerTab | BseSupplierTab) => setSearchParams(`?${new URLSearchParams({ tab })}`),
    setSelectedContractsTab: (tab: ContractsTab) => setSearchParams(`?${new URLSearchParams({ tab })}`),
    setSelectedProjectTab: (tab: ProjectTab) => setSearchParams(`?${new URLSearchParams({ tab })}`),
    setSelectedSettingsTab: (tab: SettingsTab) => setSearchParams(`?${new URLSearchParams({ tab })}`),
    setSelectedUserManagementTab: (tab: UserManagementTab) => setSearchParams(`?${new URLSearchParams({ tab })}`),
    setSelectedQuestion: (question: string) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "qa");
      params.set("question", question);
      setSearchParams(params);
    },
    setSelectedAnnouncement: (announcement: string) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "announcements");
      params.set("announcement", announcement);
      setSearchParams(params);
    },
    setSelectedContractTask: (task: string) => setSearchParams(`?${new URLSearchParams({ tab: "tasks", task })}`),
    clearSelectedContractTask: () => setSearchParams(`?${new URLSearchParams({ tab: "tasks" })}`),
    clearSelectedQuestion: () => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "qa");
      params.delete("question");
      setSearchParams(params);
    },
    clearSelectedAnnouncement: () => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "announcements");
      params.delete("announcement");
      setSearchParams(params);
    },
    clearBseDialogTab: () => {
      const params = new URLSearchParams(searchParams);
      params.delete("tab");
      params.delete("question");
      params.delete("announcement");
      params.delete("createannouncement");
      setSearchParams(params);
    },
    clearCreateAnnouncement: () => {
      const params = new URLSearchParams(searchParams);
      params.delete("createannouncement");
      setSearchParams(params);
    },
    setCreateAnnouncement: () => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "announcements");
      params.set("createannouncement", "true");
      setSearchParams(params);
    },
    get: (name: ParamNameWhiteList): string | null => searchParams.get(name),
    getContractsTab: (): ContractsTab => {
      const tab = searchParams.get("tab");
      if (
        tab === "documents" ||
        tab === "reminders" ||
        tab === "tasks" ||
        tab === "sharing" ||
        tab === "dialog" ||
        tab === "ai-chat"
      )
        return tab;
      return "info";
    },
    getEditContractStep: (): EditContractStep => {
      const step = searchParams.get("step");
      if (step === "details" || step === "docs" || step === "review") return step;
      return "details";
    },
    getProjectTab: (): ProjectTab | undefined => {
      const tab = searchParams.get("tab");
      if (tab === "documents" || tab === "products" || tab === "sourcing-events" || tab === "contracts") return tab;
      return "details";
    },
    getSettingsTab: (): SettingsTab | undefined => {
      const tab = searchParams.get("tab");
      if (
        tab === "document-templates" ||
        tab === "internal-parties" ||
        tab === "assessment-templates" ||
        tab === "standard-terms" ||
        tab === "contract-types" ||
        tab === "custom-contract-fields"
      )
        return tab;
      return "notifications";
    },
  };
};

export type EditContractStep = "details" | "docs" | "review";

export type ParamNameWhiteList =
  | "tab"
  | "question"
  | "step"
  | "secret"
  | "type"
  | "announcement"
  | "task"
  | "flow"
  | "createannouncement"
  | "search";

export type BseBuyerTab = "request" | "bids" | "evaluation" | "award" | "contract";
export type BseSupplierTab = "request" | "qa" | "announcements";
export type ContractsTab = "info" | "documents" | "reminders" | "tasks" | "sharing" | "dialog" | "ai-chat";
export type UserManagementTab = "users" | "departments" | "invites" | "azure" | "advanced" | "api";
export type ProjectTab = "details" | "documents" | "products" | "sourcing-events" | "contracts";
export type SettingsTab =
  | "notifications"
  | "document-templates"
  | "internal-parties"
  | "assessment-templates"
  | "standard-terms"
  | "contract-types"
  | "custom-contract-fields";
